import React from 'react';
import { motion } from 'framer-motion';

const FloatingElement = ({ children, className, delay = 0, duration = 6, x = 0, y = 0 }) => {
  return (
    <motion.div
      className={`absolute ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: [y, y - 15, y],
        rotate: [-2, 2, -2]
      }}
      transition={{
        opacity: { duration: 0.8, delay },
        scale: { duration: 0.8, delay },
        y: { duration, repeat: Infinity, ease: "easeInOut", delay },
        rotate: { duration: duration * 1.2, repeat: Infinity, ease: "easeInOut", delay }
      }}
    >
      {children}
    </motion.div>
  );
};

export default function FloatingElements() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Top Left - Strategy Card */}
      <FloatingElement 
        className="top-[12%] left-[8%] hidden lg:block" 
        delay={0.2}
        duration={5}
      >
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-4 shadow-2xl shadow-purple-500/30 transform -rotate-12">
          <div className="text-white text-xs font-bold mb-1">AI STRATEGY</div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-white text-lg">🤖</span>
            </div>
            <div>
              <div className="text-white/80 text-[10px]">Win Rate</div>
              <div className="text-white font-bold text-sm">87.4%</div>
            </div>
          </div>
        </div>
      </FloatingElement>

      {/* Top Right - Chart Element */}
      <FloatingElement 
        className="top-[8%] right-[10%] hidden lg:block" 
        delay={0.4}
        duration={7}
      >
        <div className="bg-white rounded-2xl p-4 shadow-2xl border border-gray-100 transform rotate-6">
          <div className="flex items-end gap-1 h-12">
            <div className="w-3 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-t h-4"></div>
            <div className="w-3 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-t h-7"></div>
            <div className="w-3 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-t h-5"></div>
            <div className="w-3 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-t h-10"></div>
            <div className="w-3 bg-gradient-to-t from-violet-500 to-purple-400 rounded-t h-12"></div>
          </div>
          <div className="text-[10px] text-gray-400 mt-2 text-center">PORTFOLIO</div>
        </div>
      </FloatingElement>

      {/* Middle Left - YES Badge */}
      <FloatingElement 
        className="top-[35%] left-[5%] hidden md:block" 
        delay={0.6}
        duration={6}
      >
        <div className="bg-gradient-to-r from-lime-400 to-green-400 text-black font-black text-2xl px-6 py-3 rounded-xl shadow-xl shadow-lime-500/30 transform -rotate-6 tracking-wider">
          YES!
        </div>
      </FloatingElement>

      {/* Middle Right - Belief Token */}
      <FloatingElement 
        className="top-[30%] right-[5%] hidden md:block" 
        delay={0.3}
        duration={5.5}
      >
        <div className="bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl p-4 shadow-2xl shadow-orange-500/30 transform rotate-12">
          <div className="text-white font-black text-xl flex items-center gap-2">
            <span>💡</span>
            <span>BELIEF</span>
          </div>
          <div className="text-white/80 text-xs mt-1">$42.50</div>
        </div>
      </FloatingElement>

      {/* Bottom Left - NO Badge */}
      <FloatingElement 
        className="bottom-[25%] left-[12%] hidden lg:block" 
        delay={0.5}
        duration={6.5}
      >
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white font-black text-xl px-5 py-2 rounded-xl shadow-xl shadow-rose-500/30 transform rotate-3">
          NO!
        </div>
      </FloatingElement>

      {/* Bottom Right - Prediction Card */}
      <FloatingElement 
        className="bottom-[20%] right-[8%] hidden lg:block" 
        delay={0.7}
        duration={5}
      >
        <div className="bg-white rounded-2xl p-4 shadow-2xl border border-gray-100 transform -rotate-6 w-48">
          <div className="text-xs text-gray-500 mb-2">Active Prediction</div>
          <div className="text-sm font-bold text-gray-800 mb-3">Will BTC hit $100K?</div>
          <div className="flex gap-2">
            <div className="flex-1 bg-emerald-100 rounded-lg py-1 text-center">
              <div className="text-emerald-600 font-bold text-xs">YES 67%</div>
            </div>
            <div className="flex-1 bg-rose-100 rounded-lg py-1 text-center">
              <div className="text-rose-600 font-bold text-xs">NO 33%</div>
            </div>
          </div>
        </div>
      </FloatingElement>

      {/* Extra floating shapes */}
      <FloatingElement 
        className="top-[55%] left-[20%] hidden xl:block" 
        delay={0.8}
        duration={8}
      >
        <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl shadow-xl shadow-cyan-500/30 transform rotate-45 flex items-center justify-center">
          <span className="transform -rotate-45 text-white text-2xl">⚡</span>
        </div>
      </FloatingElement>

      <FloatingElement 
        className="bottom-[35%] right-[20%] hidden xl:block" 
        delay={0.9}
        duration={7}
      >
        <div className="w-12 h-12 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl shadow-xl shadow-fuchsia-500/30 transform -rotate-12 flex items-center justify-center">
          <span className="text-white text-xl">📊</span>
        </div>
      </FloatingElement>
    </div>
  );
}